<template>
  <!-- Icon-only variant (MYAC-919): sits among a card's or row's own controls,
       where a labelled button would shout over the content it belongs to. Same
       wording, moved into the tooltip and the accessible name. -->
  <Button
    v-if="iconOnly"
    :icon="icon ?? 'pi pi-comments'"
    text
    size="small"
    severity="secondary"
    :aria-label="buttonLabel"
    v-tooltip.top="buttonLabel"
    @click="ask"
  />
  <Button
    v-else
    :label="buttonLabel"
    :icon="icon ?? 'pi pi-sparkles'"
    size="small"
    outlined
    @click="ask"
  />
</template>

<script setup lang="ts">
/**
 * MYAC-1013 — an in-context door into the assistant.
 *
 * The panel is general-purpose and starts blank, so using it means describing your
 * situation before you can ask anything. This button removes that step: it opens
 * the panel with the question already written, from a screen that knows what the
 * question is.
 *
 * Where it came from: all four register screens already TOLD the user to "ask the
 * chat to read it" when an extraction came back empty, and gave them no way to do
 * it — they had to go find the launcher and compose the request themselves. The
 * instruction was there; only the door was missing.
 *
 * MYAC-919 — two ways to say what the context IS, and the caller picks:
 *  - `prompt`: a question, put in the input box for the user to finish (the
 *    register prompts want a filename typed into them).
 *  - `focus`: the OBJECT on screen, attached to the conversation as a chip with
 *    questions offered beside it. Nothing is written into the input, and the
 *    reference goes out with whatever the user chooses to send.
 *
 * $$$ — neither path sends, so neither is credit-gated: the paid step is the
 * user pressing send (or a chip) in the panel, which ChatInterface already gates
 * on `isCreditsBlocked`. Opening the assistant must never cost anything — that
 * is what lets these buttons sit on every row without a second thought.
 */
import Button from 'primevue/button'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useChatPanelStore } from '@/stores/chatPanel'
import type { ChatFocusRef } from '@/stores/chatbot'

const props = defineProps<{
  /** A question to put in the input box, ready to edit and send. */
  prompt?: string
  /** Overrides the default "Ask the assistant" wording (tooltip when icon-only). */
  label?: string
  /** Render as a bare icon with the label as its tooltip. */
  iconOnly?: boolean
  /** Overrides the icon (defaults: sparkles labelled, speech bubble icon-only). */
  icon?: string
  /**
   * MYAC-919 — the object this button sits on. Attached to the conversation so
   * the agent resolves THAT one instead of inferring which a sentence means.
   */
  focus?: ChatFocusRef
}>()

/*
 * No `asked` event any more — MYAC-1287. It existed for one host: the board's
 * task dialog closed itself on press, back when a modal's mask covered the
 * panel the press had just opened. Dialogs share the row with the panel now, so
 * no host has anything to get out of the way for, and an event that only ever
 * meant "close yourself" invites the next dialog to do it again.
 */

const { t } = useI18n()

const buttonLabel = computed(() => props.label ?? t('chat.ask-assistant'))

function ask(): void {
  // Resolved on click, not at setup: this button sits on rows all over the
  // referentials screen, and none of them should have to own a pinia instance
  // just to draw one. Nothing here reads store state until it is pressed.
  const chatPanel = useChatPanelStore()
  if (props.focus) {
    chatPanel.openWithFocus(props.focus)
    return
  }
  chatPanel.openWithDraft(props.prompt ?? '')
}
</script>
